import { ChefHat } from "lucide-react";
import { motion } from "motion/react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useCustomizer } from "../../context/CustomizerContext";
import { FALLBACK_HIGHLIGHTS } from "../../data/content";
import { useProducts } from "../../hooks/useProducts";
import { iconForCategory } from "../../utils/categoryIcon";
import { Reveal } from "../ui/Reveal";

const MAX_CATEGORIES = 6;

const iconMotion = {
  hover: { rotate: [0, -12, 10, -6, 0], scale: 1.12, transition: { duration: 0.5 } },
};

/** Faixa clara com as categorias reais do cardápio (vêm da API). */
export const Categorias = () => {
  const { products, loading, error } = useProducts();
  const { builder } = useCustomizer();

  const categories = useMemo(() => {
    if (error || (!loading && products.length === 0)) {
      return [...new Set(FALLBACK_HIGHLIGHTS.map((h) => h.category))].map((name) => ({ name, count: null }));
    }
    const counts = new Map<string, number>();
    products.forEach((p) => counts.set(p.category, (counts.get(p.category) ?? 0) + 1));
    return [...counts].slice(0, MAX_CATEGORIES).map(([name, count]) => ({ name, count }));
  }, [products, loading, error]);

  return (
    <section aria-labelledby="categorias-title" className="relative z-10 -mt-14 px-4 sm:px-6 lg:px-8">
      <Reveal className="mx-auto max-w-7xl">
        <div className="rounded-[1.75rem] bg-cream p-6 text-night shadow-card sm:p-8 lg:flex lg:items-center lg:gap-10 lg:p-10">
          <div className="shrink-0 lg:w-60">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-night/60">Nossas categorias</p>
            <h2 id="categorias-title" className="mt-2 font-display text-4xl leading-none tracking-wide sm:text-5xl">
              O que vai ser <span className="text-primary-deep">hoje?</span>
            </h2>
          </div>

          <ul className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-[repeat(auto-fit,minmax(130px,1fr))] lg:mt-0 lg:flex-1">
            {loading && products.length === 0
              ? Array.from({ length: MAX_CATEGORIES }, (_, i) => (
                  <li key={i} className="flex animate-pulse flex-col items-center gap-3 p-3" aria-hidden="true">
                    <span className="size-16 rounded-full bg-night/10" />
                    <span className="h-3 w-16 rounded bg-night/10" />
                  </li>
                ))
              : categories.map(({ name, count }) => {
                  const Icon = iconForCategory(name);
                  return (
                    <motion.li key={name} whileHover="hover">
                      <Link
                        to={`/cardapio?categoria=${encodeURIComponent(name)}`}
                        className="group flex flex-col items-center gap-2.5 rounded-2xl px-1.5 py-3 text-center sm:px-3 transition-colors hover:bg-white focus-visible:bg-white focus-visible:outline-night"
                      >
                        <motion.span
                          variants={iconMotion}
                          className="grid size-16 place-items-center rounded-full bg-night text-primary transition-colors group-hover:bg-primary group-hover:text-night"
                        >
                          <Icon className="size-7" aria-hidden="true" />
                        </motion.span>
                        <span className="text-[13px] font-semibold capitalize leading-tight hyphens-auto sm:text-sm">{name}</span>
                        {count !== null && (
                          <span className="-mt-1 text-xs text-night/60">
                            {count} {count === 1 ? "item" : "itens"}
                          </span>
                        )}
                      </Link>
                    </motion.li>
                  );
                })}
            {builder && (
              <motion.li whileHover="hover">
                <Link
                  to="/monte-seu-lanche"
                  className="group flex flex-col items-center gap-2.5 rounded-2xl px-1.5 py-3 text-center transition-colors hover:bg-white focus-visible:bg-white focus-visible:outline-night sm:px-3"
                >
                  <motion.span
                    variants={iconMotion}
                    className="grid size-16 place-items-center rounded-full bg-primary text-night transition-colors group-hover:bg-night group-hover:text-primary"
                  >
                    <ChefHat className="size-7" aria-hidden="true" />
                  </motion.span>
                  <span className="text-[13px] font-semibold leading-tight sm:text-sm">Monte o seu</span>
                  <span className="-mt-1 text-xs text-night/60">do seu jeito</span>
                </Link>
              </motion.li>
            )}
          </ul>
        </div>
      </Reveal>
    </section>
  );
};
